"use client"

import { useState } from "react"

import { ConversationCardDeck } from "@/components/collabs/conversation-card-deck"
import type { TrackPrompt } from "@/types"
import type { TrackPromptSummary } from "@/types/contents"

/**
 * PromptDisplayAsCards previews a collab's prompts(without soft deleted) through the same
 * `ConversationCardDeck` the community cycles in the suggestion composer.
 * @param trackPrompts - Prompts to preview.
 * @returns The card deck, or a placeholder when there are no prompts.
 */
export function PromptDisplayAsCards({
  trackPrompts,
}: {
  trackPrompts: TrackPromptSummary[]
}) {
  const sortedPrompts = [...trackPrompts]
    .sort((a, b) => a.position - b.position)
    .flatMap(toConversationPrompt)

  const [deck, setDeck] = useState({ index: 0, direction: 0 })
  const safeIndex =
    sortedPrompts.length > 0
      ? Math.min(deck.index, sortedPrompts.length - 1)
      : 0

  if (sortedPrompts.length === 0) {
    return (
      <p className="text-tertiary py-6 text-center text-sm">No prompts yet.</p>
    )
  }

  const navigateDeck = (direction: 1 | -1) => {
    setDeck({
      index: wrap(safeIndex + direction, sortedPrompts.length),
      direction,
    })
  }

  return (
    <ConversationCardDeck
      prompts={sortedPrompts}
      index={safeIndex}
      direction={deck.direction}
      onNavigate={navigateDeck}
    />
  )
}

function wrap(value: number, length: number) {
  return ((value % length) + length) % length
}

// ConversationCard only reads content_type/position/title/guidance/
// suggestions_count — the rest of TrackPrompt's shape is for the public
// suggestion flow, so it's filled with inert placeholders here.
function toConversationPrompt(prompt: TrackPromptSummary): TrackPrompt[] {
  if (prompt.deleted_at) return []

  return [
    {
      ...prompt,
      status: {
        state: "empty",
        suggestion_count: prompt.suggestions_count,
        accepted_contributor_name: null,
        can_suggest: false,
      },
      track_suggestions: [],
    },
  ]
}
